import React, { useState, useEffect } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '../../../../components/ui';
import { FiFileText as FileText, FiUpload as Upload, FiDownload as Download, FiTrash2 as Trash2, FiLink as Link, FiExternalLink as ExternalLink, FiFile as File, FiEdit2 as Edit } from 'react-icons/fi';
import toast from 'react-hot-toast';
import { projectAPI } from '../../../../services/api';
import { usePermissions } from '../../../../hooks/usePermissions';
import { PERMISSIONS } from '../../../../utils/permissions';
import { TransformedProject } from '../hooks/useProjects';

interface GoogleDriveFile {
  id: string;
  name: string;
  mimeType: string;
  size?: number;
  viewLink?: string;
  downloadLink?: string;
}

interface Props {
  project: TransformedProject;
  onUpdate?: () => void;
}

export const SoWSection: React.FC<Props> = ({ project, onUpdate }) => {
  const { hasPermission } = usePermissions();
  const [uploading, setUploading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [showGoogleDriveInput, setShowGoogleDriveInput] = useState(false);
  const [googleDriveUrl, setGoogleDriveUrl] = useState('');
  const [savingGoogleDrive, setSavingGoogleDrive] = useState(false);
  const [googleDriveFiles, setGoogleDriveFiles] = useState<GoogleDriveFile[]>([]);
  const [loadingFiles, setLoadingFiles] = useState(false);
  const [filesError, setFilesError] = useState<string | null>(null);

  const isPartner =
    hasPermission(PERMISSIONS.PARTNERS_VIEW) ||
    hasPermission(PERMISSIONS.PARTNERS_MANAGE);
  const hasFileUpload = project.has_sow && !project.sow_google_drive_url;
  const hasGoogleDrive = !!project.sow_google_drive_url;

  const handleUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (file.type !== 'application/pdf') {
      toast.error('Please upload a PDF file');
      return;
    }

    const maxSize = 10 * 1024 * 1024;
    if (file.size > maxSize) {
      toast.error('File size must be less than 10MB');
      return;
    }

    setUploading(true);

    try {
      const uploadUrlResponse = await projectAPI.getSoWUploadUrl(project.id);
      if (!uploadUrlResponse.success) {
        throw new Error(uploadUrlResponse.error || 'Failed to get upload URL');
      }

      const { upload_url } = uploadUrlResponse.data;

      const uploadResponse = await fetch(upload_url, {
        method: 'PUT',
        body: file,
        headers: {
          'Content-Type': 'application/pdf'
        }
      });

      if (!uploadResponse.ok) {
        throw new Error('Failed to upload file to S3');
      }

      const confirmResponse = await projectAPI.confirmSoWUpload(project.id);
      if (!confirmResponse.success) {
        throw new Error(confirmResponse.error || 'Failed to confirm upload');
      }

      toast.success('Statement of Work uploaded successfully');

      if (onUpdate) {
        onUpdate();
      }
    } catch (err: unknown) {
      console.error('Error uploading SoW:', err);
      const message = err instanceof Error ? err.message : 'Failed to upload Statement of Work';
      toast.error(message);
    } finally {
      setUploading(false);
      event.target.value = '';
    }
  };

  const handleDownload = async () => {
    setDownloading(true);

    try {
      const response = await projectAPI.getSoWDownloadUrl(project.id);
      if (!response.success) {
        throw new Error(response.error || 'Failed to get download URL');
      }

      const { download_url } = response.data;
      window.open(download_url, '_blank');
    } catch (err: unknown) {
      console.error('Error downloading SoW:', err);
      const message = err instanceof Error ? err.message : 'Failed to download Statement of Work';
      toast.error(message);
    } finally {
      setDownloading(false);
    }
  };

  const handleDelete = async () => {
    if (!globalThis.confirm('Are you sure you want to delete the Statement of Work? This action cannot be undone.')) {
      return;
    }

    setDeleting(true);

    try {
      const response = await projectAPI.deleteSoW(project.id);
      if (!response.success) {
        throw new Error(response.error || 'Failed to delete Statement of Work');
      }

      toast.success('Statement of Work deleted successfully');

      if (onUpdate) {
        onUpdate();
      }
    } catch (err: unknown) {
      console.error('Error deleting SoW:', err);
      const message = err instanceof Error ? err.message : 'Failed to delete Statement of Work';
      toast.error(message);
    } finally {
      setDeleting(false);
    }
  };

  const handleShowGoogleDriveInput = () => {
    setShowGoogleDriveInput(true);
    setGoogleDriveUrl(project.sow_google_drive_url ?? '');
  };

  const handleCancelGoogleDrive = () => {
    setShowGoogleDriveInput(false);
    setGoogleDriveUrl('');
  };

  const handleSaveGoogleDrive = async () => {
    if (!googleDriveUrl.trim()) {
      toast.error('Please enter a Google Drive URL');
      return;
    }

    if (!googleDriveUrl.startsWith('https://drive.google.com/')) {
      toast.error('Please enter a valid Google Drive URL');
      return;
    }

    setSavingGoogleDrive(true);

    try {
      const response = await projectAPI.setGoogleDriveLink(project.id, googleDriveUrl.trim());
      if (!response.success) {
        throw new Error(response.error || 'Failed to set Google Drive link');
      }

      toast.success('Google Drive link saved successfully');
      setShowGoogleDriveInput(false);
      setGoogleDriveUrl('');

      if (onUpdate) {
        onUpdate();
      }
    } catch (err: unknown) {
      console.error('Error saving Google Drive link:', err);
      const message = err instanceof Error ? err.message : 'Failed to save Google Drive link';
      toast.error(message);
    } finally {
      setSavingGoogleDrive(false);
    }
  };

  const handleRemoveGoogleDrive = async () => {
    if (!globalThis.confirm('Are you sure you want to remove the Google Drive link?')) {
      return;
    }

    setDeleting(true);

    try {
      const response = await projectAPI.removeGoogleDriveLink(project.id);
      if (!response.success) {
        throw new Error(response.error || 'Failed to remove Google Drive link');
      }

      toast.success('Google Drive link removed successfully');
      setGoogleDriveFiles([]);
      setFilesError(null);

      if (onUpdate) {
        onUpdate();
      }
    } catch (err: unknown) {
      console.error('Error removing Google Drive link:', err);
      const message = err instanceof Error ? err.message : 'Failed to remove Google Drive link';
      toast.error(message);
    } finally {
      setDeleting(false);
    }
  };

  useEffect(() => {
    const loadGoogleDriveFiles = async () => {
      if (!hasGoogleDrive) {
        setGoogleDriveFiles([]);
        setFilesError(null);
        return;
      }

      setLoadingFiles(true);
      setFilesError(null);

      try {
        const response = await projectAPI.getGoogleDriveFiles(project.id);
        if (!response.success) {
          throw new Error(response.error || 'Failed to load Google Drive files');
        }

        const { files, message } = response.data;
        setGoogleDriveFiles(files ?? []);

        if (message) {
          setFilesError(message);
        }
      } catch (err: unknown) {
        console.error('Error loading Google Drive files:', err);
        const message = err instanceof Error ? err.message : 'Failed to load files from Google Drive';
        setFilesError(message);
        setGoogleDriveFiles([]);
      } finally {
        setLoadingFiles(false);
      }
    };

    loadGoogleDriveFiles();
  }, [hasGoogleDrive, project.id, project.sow_google_drive_url]);

  const formatFileSize = (bytes?: number): string => {
    if (!bytes) return 'Unknown size';
    const kb = bytes / 1024;
    const mb = kb / 1024;
    if (mb >= 1) return `${mb.toFixed(2)} MB`;
    return `${kb.toFixed(2)} KB`;
  };

  const getEditLink = (file: GoogleDriveFile): string => {
    const fileId = file.id;
    const mimeType = file.mimeType;

    if (mimeType === 'application/vnd.google-apps.document') {
      return `https://docs.google.com/document/d/${fileId}/edit`;
    }
    if (mimeType === 'application/vnd.google-apps.spreadsheet') {
      return `https://docs.google.com/spreadsheets/d/${fileId}/edit`;
    }
    if (mimeType === 'application/vnd.google-apps.presentation') {
      return `https://docs.google.com/presentation/d/${fileId}/edit`;
    }
    if (file.viewLink) {
      return file.viewLink;
    }
    return `https://drive.google.com/file/d/${fileId}/view`;
  };

  const renderFileList = (files: GoogleDriveFile[]) => (
    <div className="border border-gray-200 dark:border-gray-700 rounded-md overflow-hidden">
      <div className="bg-gray-50 dark:bg-gray-800 px-4 py-2 border-b border-gray-200 dark:border-gray-700">
        <p className="text-xs font-medium text-gray-700 dark:text-gray-300">
          {files.length} file{files.length === 1 ? '' : 's'} available
        </p>
      </div>
      <div className="divide-y divide-gray-200 dark:divide-gray-700">
        {files.map((file) => (
          <div
            key={file.id}
            className="flex items-center justify-between gap-3 px-4 py-3 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
          >
            <div className="flex items-center gap-3 flex-1 min-w-0">
              <File className="w-5 h-5 text-gray-400 dark:text-gray-500 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                  {file.name}
                </p>
                {file.size && (
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    {formatFileSize(file.size)}
                  </p>
                )}
              </div>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              {isPartner && (
                <a
                  href={getEditLink(file)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="p-2 text-green-600 hover:text-green-700 dark:text-green-400 dark:hover:text-green-300 hover:bg-green-50 dark:hover:bg-green-900/20 rounded transition-colors"
                  title="Edit in Google Drive"
                >
                  <Edit className="w-4 h-4" />
                </a>
              )}
              {file.downloadLink && (
                <a
                  href={file.downloadLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="p-2 text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded transition-colors"
                  title="Download file"
                >
                  <Download className="w-4 h-4" />
                </a>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  let uploadButtonLabel: string;
  if (uploading) {
    uploadButtonLabel = 'Uploading...';
  } else if (hasFileUpload) {
    uploadButtonLabel = 'Replace File';
  } else {
    uploadButtonLabel = 'Upload File';
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <FileText className="w-5 h-5" />
            Statement of Work
          </CardTitle>
          {project.has_sow && (
            <span className="px-2 py-1 text-xs font-medium rounded-full bg-green-100 dark:bg-green-900/50 text-green-800 dark:text-green-300">
              Available
            </span>
          )}
        </div>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {/* Status message */}
          <p className="text-sm text-gray-600 dark:text-gray-400">
            {hasGoogleDrive && isPartner && (
              <>Google Drive folder is linked for this project&apos;s Statement of Work.</>
            )}
            {hasGoogleDrive && !isPartner && (
              <>Please find below the files uploaded to your project folder by {project.partner_name ?? 'your partner'}.</>
            )}
            {hasFileUpload && (
              <>The Statement of Work document is available for download.</>
            )}
            {!project.has_sow && (
              <>No Statement of Work has been uploaded or linked for this project yet.</>
            )}
          </p>

          {/* Google Drive folder display - Partner only */}
          {hasGoogleDrive && isPartner && (
            <div className="space-y-3">
              <div className="p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-md">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-blue-900 dark:text-blue-200 mb-1">
                      Google Drive Folder
                    </p>
                    <p className="text-xs text-blue-700 dark:text-blue-300 break-all">
                      {project.sow_google_drive_url}
                    </p>
                  </div>
                  <a
                    href={project.sow_google_drive_url ?? undefined}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex-shrink-0 p-2 text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300 hover:bg-blue-100 dark:hover:bg-blue-900/40 rounded transition-colors"
                    title="Open in Google Drive"
                  >
                    <ExternalLink className="w-5 h-5" />
                  </a>
                </div>
              </div>

              {loadingFiles && (
                <div className="text-sm text-gray-600 dark:text-gray-400 text-center py-4">
                  Loading files...
                </div>
              )}

              {!loadingFiles && filesError && (
                <div className="p-3 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-md">
                  <p className="text-sm text-yellow-800 dark:text-yellow-200">
                    {filesError}
                  </p>
                  <p className="text-xs text-yellow-700 dark:text-yellow-300 mt-1">
                    You can still open the folder directly using the link above.
                  </p>
                </div>
              )}

              {!loadingFiles && !filesError && googleDriveFiles.length === 0 && (
                <div className="text-sm text-gray-600 dark:text-gray-400 text-center py-4">
                  No files found in this folder.
                </div>
              )}

              {!loadingFiles && googleDriveFiles.length > 0 && renderFileList(googleDriveFiles)}
            </div>
          )}

          {/* Files list for non-partners */}
          {hasGoogleDrive && !isPartner && (
            <div className="space-y-3">
              {loadingFiles && (
                <div className="text-sm text-gray-600 dark:text-gray-400 text-center py-4">
                  Loading files...
                </div>
              )}

              {!loadingFiles && filesError && (
                <div className="p-3 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-md">
                  <p className="text-sm text-yellow-800 dark:text-yellow-200">
                    Unable to load files at this time. Please contact {project.partner_name ?? 'your partner'} for assistance.
                  </p>
                </div>
              )}

              {!loadingFiles && !filesError && googleDriveFiles.length === 0 && (
                <div className="text-sm text-gray-600 dark:text-gray-400 text-center py-4">
                  No files are currently available.
                </div>
              )}

              {!loadingFiles && googleDriveFiles.length > 0 && renderFileList(googleDriveFiles)}
            </div>
          )}

          {/* Google Drive input form (Partner only) */}
          {isPartner && showGoogleDriveInput && (
            <div className="p-4 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-md space-y-3">
              <div>
                <label htmlFor="google-drive-folder-url" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Google Drive Folder URL
                </label>
                <input id="google-drive-folder-url"
                  type="url"
                  value={googleDriveUrl}
                  onChange={(e) => setGoogleDriveUrl(e.target.value)}
                  placeholder="https://drive.google.com/drive/folders/..."
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  disabled={savingGoogleDrive}
                />
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  Enter the shareable link to your Google Drive folder containing SoW documents.
                </p>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={handleSaveGoogleDrive}
                  disabled={savingGoogleDrive}
                  className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {savingGoogleDrive ? 'Saving...' : 'Save Link'}
                </button>
                <button
                  onClick={handleCancelGoogleDrive}
                  disabled={savingGoogleDrive}
                  className="px-4 py-2 text-sm font-medium border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}

          {/* Action buttons */}
          <div className="flex gap-3 flex-wrap">
            {isPartner && !showGoogleDriveInput && (
              <label className={`px-4 py-2 text-sm font-medium rounded-md cursor-pointer inline-flex items-center gap-2 ${
                uploading
                  ? 'bg-blue-400 text-white cursor-not-allowed'
                  : 'text-white bg-blue-600 hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600'
              }`}>
                <Upload className="w-4 h-4" />
                {uploadButtonLabel}
                <input
                  type="file"
                  accept="application/pdf"
                  onChange={handleUpload}
                  disabled={uploading}
                  className="hidden"
                />
              </label>
            )}

            {isPartner && !showGoogleDriveInput && (
              <button
                onClick={handleShowGoogleDriveInput}
                className="px-4 py-2 text-sm font-medium border border-blue-600 dark:border-blue-500 text-blue-600 dark:text-blue-400 rounded-md hover:bg-blue-50 dark:hover:bg-blue-900/20 inline-flex items-center gap-2"
              >
                <Link className="w-4 h-4" />
                {hasGoogleDrive ? 'Update Google Drive Link' : 'Link Google Drive Folder'}
              </button>
            )}

            {hasFileUpload && (
              <button
                onClick={handleDownload}
                disabled={downloading}
                className="px-4 py-2 text-sm font-medium border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-2"
              >
                <Download className="w-4 h-4" />
                {downloading ? 'Loading...' : 'Download File'}
              </button>
            )}

            {hasGoogleDrive && isPartner && (
              <a
                href={project.sow_google_drive_url ?? undefined}
                target="_blank"
                rel="noopener noreferrer"
                className="px-4 py-2 text-sm font-medium border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 inline-flex items-center gap-2"
              >
                <ExternalLink className="w-4 h-4" />
                Open Google Drive
              </a>
            )}

            {isPartner && hasFileUpload && !showGoogleDriveInput && (
              <button
                onClick={handleDelete}
                disabled={deleting}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700 dark:bg-red-500 dark:hover:bg-red-600 disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-2"
              >
                <Trash2 className="w-4 h-4" />
                {deleting ? 'Deleting...' : 'Delete File'}
              </button>
            )}

            {isPartner && hasGoogleDrive && !showGoogleDriveInput && (
              <button
                onClick={handleRemoveGoogleDrive}
                disabled={deleting}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700 dark:bg-red-500 dark:hover:bg-red-600 disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-2"
              >
                <Trash2 className="w-4 h-4" />
                {deleting ? 'Removing...' : 'Remove Link'}
              </button>
            )}
          </div>

          {!isPartner && !project.has_sow && (
            <p className="text-xs text-gray-500 dark:text-gray-400 italic">
              Contact your partner to upload a Statement of Work document or provide a Google Drive folder link.
            </p>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default SoWSection;
